﻿import useSponsors from "@/hooks/useSponsors";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, Button, Container, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sponsor } from "../../api/dtos";
import { AdminRoutes } from "../../constants/routes";
import { useRegistrationDeskSessions } from "../../hooks/useRegistrationDeskSessions";
import { getFormRoute } from "../../utils/getComputedFormRoutes";
import DeleteSponsorModal from "../common/DeleteSponsorModal";
import ExtendedTableCell from "../common/ExtendedTableCell";
import Loading from "../common/Loading";
import AdminNavigation from "./AdminNavigation";

const SponsorLevel = [
    "",
    "None",
    "Wifi Only",
    "Bronze",
    "Silver",
    "Gold",
    "Diamond",
    "Platinum"
];

const AdminSponsorHome: React.FunctionComponent = () => {

    const navigate = useNavigate();

    const theme = useTheme();

    const [sponsor, setSponsor] = useState<Sponsor | null>();

    const sessionData = useRegistrationDeskSessions();

    const [sponsorDesk, setSponsorDesk] = useState<boolean>(false);

    const { sponsors, count, loading, refresh: refreshRegistrations } = useSponsors();

    React.useEffect(() => {
        setSponsorDesk(JSON.parse(window.localStorage.getItem('sponsorDesk') ?? "false"));
    }, []);

    const handleNewClick = () => {
        navigate(AdminRoutes.ADMIN_ADD_SPONSOR);
    }

    const handleRegistrationClick = (id: number) => {
        navigate(getFormRoute(AdminRoutes.ADMIN_SPONSOR_BY_ID, id.toString()))
    }

    const handleDelete = (sponsor: Sponsor) => {
        setSponsor(sponsor)
    }

    const handleClose = (refetch: boolean) => {
        setSponsor(null);

        if (refetch) {
            refreshRegistrations()
        }
    }

    
    return (
        <Box>
            <AdminNavigation/>
            <DeleteSponsorModal 
                sponsor={sponsor}
                handleModalClose={handleClose}
            />
            <Box>
                <Container
                    maxWidth={false}
                >
                    <Box
                        sx={{
                            //...body,
                            px: {
                                xs: 1
                            }
                        }}
                    >
                        <Grid
                            container
                            justifyContent="space-between"
                            sx={{
                                mb: {
                                    xs: 2
                                },
                                mt: {
                                    xs: 5
                                }
                            }}
                        >
                            <Grid 
                                item
                                xs={8}
                            >
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    gutterBottom
                                >
                                    Sponsors
                                </Typography>
                            </Grid>
                            <Grid 
                                item
                                xs={2}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'end'
                                }}
                            >
                                {
                                    !sponsorDesk && (
                                        <Tooltip placement="right" title="New Sponsor">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleNewClick}
                                                sx={{
                                                    p: {
                                                        xs: 1
                                                    },
                                                    minWidth: 48,
                                                    maxHeight: 48,
                                                    height: '100%',
                                                    fontSize: '16px',
                                                    borderColor: 'primary.main',
                                                    ":hover": {
                                                        borderColor: 'secondary.main',
                                                        backgroundColor: 'secondary.main',
                                                        color: '#FFF'
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                            </Button>
                                        </Tooltip>
                                    )
                                }
                            </Grid>
                        </Grid>




                        {
                            sessionData?.loading ?
                                <Loading />
                            : !loading && (!sponsors || sponsors?.length === 0) ?
                                <Alert
                                    severity="info"
                                    icon={<FontAwesomeIcon fixedWidth icon={faExclamationCircle} />}
                                >
                                    There are no sponsors
                                </Alert>
                                :
                                <>
                                    <TableContainer component={Paper}>
                                        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    
                                                    <ExtendedTableCell
                                                    >
                                                        Entered
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                    >
                                                        Sponsors Name
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                    >
                                                        Sponsor Level
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                    >
                                                        Symposiums
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                    >
                                                        Training Day
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                    >
                                                        Free Registrations
                                                    </ExtendedTableCell>

                                                    <ExtendedTableCell
                                                    >
                                                        Free Registrations Used
                                                    </ExtendedTableCell>
                                                    
                                                    <ExtendedTableCell
                                                        justify="center"
                                                    >
                                                        Delete
                                                    </ExtendedTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    loading ?
                                                        <Loading />
                                                        :
                                                        <>
                                                            {
                                                                <>
                                                                    {
                                                                        sponsors && sponsors?.map((sponsor) => {
                                                                            return <TableRow
                                                                                key={`sponsor-${sponsor.id}`}
                                                                                sx={{
                                                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                                                    ":hover": {
                                                                                        cursor: 'pointer',
                                                                                        //backgroundColor: registration.sessionAttendance?.every(s => s.attendance !== true) ? noSessionsHover : registration.amountOwing === 0 ? theme.palette.grey[100] : errorLightHover
                                                                                    },
                                                                                    //backgroundColor: registration.sessionAttendance?.every(s => s.attendance !== true) ? noSessions : registration.amountOwing === 0 ? 'inherit' : errorLight 
                                                                                }}
                                                                                onClick={() => handleRegistrationClick(sponsor.id!)}
                                                                            >
                                                                                {
                                                                                    !sponsorDesk && (
                                                                                        <>
                                                                                            <TableCell>
                                                                                                {
                                                                                                    sponsor?.enteredDate && new Date(sponsor.enteredDate).toLocaleDateString('en-GB')
                                                                                                }
                                                                                            </TableCell>
                                                                                        </>
                                                                                    )
                                                                                }
                                                                                <TableCell>
                                                                                    {sponsor.sponsorName}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {SponsorLevel[sponsor.sponsorLevel ?? 1]}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {sponsor.symposiums? 1 : 0}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {sponsor.traineeAndEarlyCareerDay? 1 : 0}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {sponsor.freeRegistrations}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {sponsor.registrationsUsed ?? 0}
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    onClick={event => {
                                                                                        handleDelete(sponsor);
                                                                                        event.stopPropagation();
                                                                                    }}
                                                                                    sx={{
                                                                                        textAlign: 'center',
                                                                                        color: theme.palette.primary.main,
                                                                                        '&:hover':{
                                                                                            color: theme.palette.secondary.main,
                                                                                            backgroundColor: theme.palette.grey[300]  
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <FontAwesomeIcon size="lg" fixedWidth icon={faTrash} />
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        })
                                                                    }
                                                                </>
                                                            }
                                                        </>
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                        }
                    </Box>
                </Container>
            </Box>
        </Box>
    )
}

export default AdminSponsorHome
